/**
 * useEmailCheck Hook
 *
 * Encapsulates email duplicate checking and session recovery logic
 * extracted from PartyDetails.
 *
 * Responsibilities:
 * - Check if an email is already registered (on blur)
 * - Check for existing registration session to offer recovery
 * - Manage session recovery modal state
 */

import { useState, useCallback } from 'react';
import { checkEmailExists } from '../services/c1OnboardingService';
import { resumeRegistrationSession } from '../services/registrationSessionService';

export interface SessionData {
  currentStep: string;
  email?: string;
  businessDetails?: Record<string, unknown>;
  reviewSign?: Record<string, unknown>;
  planSelection?: Record<string, unknown>;
  paymentDetails?: Record<string, unknown>;
}

export interface UseEmailCheckOptions {
  /** The current email in the parent form's saved data (to skip re-checking) */
  savedEmail?: string;
  /** Callback when a previous session is found and user wants to restore */
  onSessionFound?: (session: SessionData) => void;
}

export interface UseEmailCheckReturn {
  /** Whether the email duplicate check is in progress */
  isCheckingEmail: boolean;
  /** Whether the session recovery check is in progress */
  isCheckingSession: boolean;
  /** Error message from email check (e.g. "already registered") */
  emailError: string;
  /** Whether to show the session recovery modal */
  showSessionModal: boolean;
  /** The found session data (if any) */
  foundSession: SessionData | null;
  /** Run the full email check (duplicate + session recovery). Call on blur. */
  checkEmail: (email: string) => Promise<void>;
  /** User chose to restore the found session */
  handleRestoreSession: () => void;
  /** User chose to start fresh (dismiss modal) */
  handleStartFresh: () => void;
  /** Clear any email error (e.g. when user edits the field) */
  clearError: () => void;
}

export function useEmailCheck(options: UseEmailCheckOptions = {}): UseEmailCheckReturn {
  const { savedEmail, onSessionFound } = options;

  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [foundSession, setFoundSession] = useState<SessionData | null>(null);

  const checkEmail = useCallback(async (email: string) => {
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    // 1. Duplicate check
    setIsCheckingEmail(true);
    try {
      const result = await checkEmailExists(email);
      if (result.exists) {
        setEmailError(
          result.message || 'This email is already registered with an existing organization. Please use a different email.'
        );
        setIsCheckingEmail(false);
        return; // Don't check session if already registered
      }
      setEmailError('');
    } catch {
      // Non-blocking
    } finally {
      setIsCheckingEmail(false);
    }

    // 2. Session recovery check
    if (showSessionModal) return;
    if (!onSessionFound) return;
    if (savedEmail === email) return;
    if (foundSession?.email === email.toLowerCase().trim()) return;

    setIsCheckingSession(true);
    try {
      const response = await resumeRegistrationSession(email);
      if (response.found && response.session) {
        setFoundSession(response.session as unknown as SessionData);
        setShowSessionModal(true);
      }
    } catch {
      // Non-blocking
    } finally {
      setIsCheckingSession(false);
    }
  }, [savedEmail, onSessionFound, showSessionModal, foundSession?.email]);

  const handleRestoreSession = useCallback(() => {
    if (foundSession && onSessionFound) {
      onSessionFound(foundSession);
    }
    setShowSessionModal(false);
    setFoundSession(null);
  }, [foundSession, onSessionFound]);

  const handleStartFresh = useCallback(() => {
    setShowSessionModal(false);
    setFoundSession(null);
  }, []);

  const clearError = useCallback(() => {
    setEmailError('');
  }, []);

  return {
    isCheckingEmail,
    isCheckingSession,
    emailError,
    showSessionModal,
    foundSession,
    checkEmail,
    handleRestoreSession,
    handleStartFresh,
    clearError,
  };
}
